import * as React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Avatar, Button, Paper, Stack } from '@mui/material';
import { WidgetFooter, WidgetHeader } from 'pages/WidgetManagement/common';
import { HelpCenter } from '@mui/icons-material';
import { CustomTransComponent, GroupPath } from 'components';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useTypedSelector } from '@fiji/common';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { hasValidCurrentScore, MAX_SCORE } from '@fiji/common/src/features/score/scoreHelpers';
import type { JSX } from 'react';

type ScoreWidgetProps = {
    widgetData: any;
    mode: string;
    learnMoreCallback: () => void;
};

type ScoreWidgetAccessProps = {
    canViewInsight: boolean;
};

const ScoreDial = ({ scoreData }: { scoreData: any }): JSX.Element => (
    <Stack justifyContent={'center'} alignItems={'center'} data-testid="score-widget-dial">
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
                variant="determinate"
                sx={{ color: `${scoreData?.scoreColor}70` }}
                size={124}
                thickness={8}
                value={MAX_SCORE}
            />
            <CircularProgress
                variant="determinate"
                sx={{ position: 'absolute', left: 0, color: scoreData?.scoreColor }}
                size={124}
                thickness={8}
                value={scoreData?.currentScore}
            />
            <Box
                sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography variant="h2" color={scoreData?.scoreColor} fontSize={'40px'} fontWeight={'700'}>
                    {scoreData?.grade}
                </Typography>
            </Box>
        </Box>
    </Stack>
);

const LearnMoreButton = ({
    mode,
    variant,
    onClick,
}: {
    mode: string;
    variant: 'contained' | 'outlined';
    onClick: () => void;
}): JSX.Element => (
    <Button data-testid="score-widget-learn-more" disabled={mode !== 'view'} variant={variant} onClick={onClick}>
        <CustomTransComponent translationKey={'WIDGETS:SCORE.LEARN_MORE'} />
    </Button>
);

const ScoreSummary = ({
    scoreData,
    multiDevice,
    name,
    groupPath,
}: {
    scoreData: any;
    multiDevice?: boolean;
    name?: string;
    groupPath?: any[];
}): JSX.Element => {
    const maxScore = MAX_SCORE;

    return (
        <Stack data-testid="score-widget-summary" m={0}>
            {multiDevice && <GroupPath deviceName={name} pathName={groupPath ?? []} />}
            <Typography variant="h1" fontSize={'34px'}>
                <span className="color-content fw-600 f-34">{`${Math.round(scoreData?.currentScore)}`}</span>{' '}
                <CustomTransComponent translationKey={'WIDGETS:SCORE.OF'} /> {maxScore}
            </Typography>
            <Typography variant="h6" fontSize={'20px'} fontWeight={'600'} mb={1}>
                {scoreData?.scoreName}
            </Typography>
        </Stack>
    );
};

const ScoreContent = ({
    widgetData,
    mode,
    learnMoreCallback,
    canViewInsight,
}: ScoreWidgetProps & ScoreWidgetAccessProps): JSX.Element => {
    const { data: scoreData, selectedNodes, multiDevice } = widgetData ?? {};
    const { name, groupPath } = selectedNodes?.[0] ?? {};

    const contentHeight = mode === 'edit' || widgetData?.viewAllButton ? '97px' : '49px';

    return (
        <Stack data-testid="score-widget-content" sx={{ height: `calc(100% - ${contentHeight})`, overflow: 'auto' }}>
            <Stack
                sx={{
                    background: `linear-gradient(270deg, ${scoreData?.scoreColor}30 0%, rgba(231, 246, 228, 0) 100%)`,
                    minHeight: multiDevice ? '220px' : '205px',
                }}
                direction={'row'}
                py={4}
                px={2}
                spacing={3}
                className="border-bottom-1 border-top-1"
            >
                <ScoreDial scoreData={scoreData} />
                <Stack spacing={1} data-testid="score-widget-summary-and-actions" sx={{ flex: 1 }}>
                    <ScoreSummary scoreData={scoreData} multiDevice={multiDevice} name={name} groupPath={groupPath} />
                    {widgetData?.isLearnMoreVisible && canViewInsight && (
                        <LearnMoreButton mode={mode} variant="outlined" onClick={learnMoreCallback} />
                    )}
                </Stack>
            </Stack>
            {widgetData?.isDescriptionVisible && (
                <Stack p={2} data-testid="score-widget-description">
                    <Typography variant="body2">
                        <CustomTransComponent translationKey={'WIDGETS:SCORE.DESCRIPTION'} components={{ b: <b /> }} />
                    </Typography>
                </Stack>
            )}
        </Stack>
    );
};

const ScoreEmptyState = ({
    widgetData,
    mode,
    learnMoreCallback,
    canViewInsight,
    isUnsupportedGrade,
}: ScoreWidgetProps & ScoreWidgetAccessProps & { isUnsupportedGrade: boolean }): JSX.Element => (
    <CustomEmptyState
        data-testid="score-widget-empty-state"
        className="padding-y-96"
        icon={
            <Avatar sx={{ width: '56px', height: ' 56px' }}>
                <HelpCenter className="height-76 width-76 color-icon" />
            </Avatar>
        }
        title={<CustomTransComponent translationKey={'WIDGETS:SCORE.NOT_AVAILABLE'} />}
        description={
            isUnsupportedGrade ? <CustomTransComponent translationKey={'WIDGETS:SCORE.NOT_SUPPORTED'} /> : undefined
        }
        actions={
            widgetData?.isLearnMoreVisible &&
            canViewInsight && <LearnMoreButton mode={mode} variant="contained" onClick={learnMoreCallback} />
        }
    />
);

const Component = ({ widgetData, mode, learnMoreCallback }: ScoreWidgetProps): JSX.Element => {
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canViewInsight =
        hasPermission('health-score-insights_view-user-widgets') ||
        hasPermission('health-score-insights_view-system-widgets');
    const isUnsupportedGrade =
        typeof widgetData?.data?.grade === 'string' && widgetData.data.grade.trim().toLowerCase() === 'notsupported';
    const hasValidScore =
        widgetData?.data?.grade !== null && !isUnsupportedGrade && hasValidCurrentScore(widgetData?.data);

    useNewCustomTranslations('WIDGETS');

    return (
        <Paper sx={{ height: '100%' }} data-testid="score-widget-root">
            <WidgetHeader mode={mode} widgetData={widgetData} />
            {hasValidScore ? (
                <ScoreContent
                    widgetData={widgetData}
                    mode={mode}
                    learnMoreCallback={learnMoreCallback}
                    canViewInsight={canViewInsight}
                />
            ) : (
                <ScoreEmptyState
                    widgetData={widgetData}
                    mode={mode}
                    learnMoreCallback={learnMoreCallback}
                    canViewInsight={canViewInsight}
                    isUnsupportedGrade={isUnsupportedGrade}
                />
            )}
            <WidgetFooter widgetData={widgetData} ids={[]} mode={mode} disabled={true} />
        </Paper>
    );
};

export default Component;
